import React from "react";
import { Target, TrendingUp, TrendingDown, Minus, Calculator, Loader2, CheckCircle2 } from "lucide-react";
import { TargetReport, TargetReportRow } from "../types";
import { formatRubles } from "../utils/math_helpers";

/** Рекомендованный баланс по форме 0710001 (полный порядок строк Актив/Пассив)
 *  + целевой ОФР (0710002) с ростом. onRecalc — пересчитать методики по варианту. */
export default function TargetReportCard({ report, title, onRecalc, recalcBusy }: {
  report: TargetReport;
  title?: string;
  onRecalc?: () => void;
  recalcBusy?: boolean;
}) {
  const money = (v?: number | null) => (v === null || v === undefined ? "—" : formatRubles(v));
  const deltaCell = (d?: number | null) => {
    if (d === null || d === undefined) return <span className="text-slate-300">—</span>;
    const Icon = d === 0 ? Minus : d > 0 ? TrendingUp : TrendingDown;
    const color = d === 0 ? "text-slate-400" : d > 0 ? "text-emerald-600" : "text-rose-500";
    return <span className={`inline-flex items-center gap-1 justify-end font-mono ${color}`}><Icon className="w-3 h-3" />{d > 0 ? "+" : ""}{formatRubles(d)}</span>;
  };

  const rowEl = (r: TargetReportRow, i: number) => {
    if (r.kind === "section")
      return <tr key={i}><td colSpan={4} className="py-1.5 px-3 bg-slate-100/70 font-bold text-slate-700 text-[11px] uppercase tracking-wider">{r.title}</td></tr>;
    if (r.kind === "head")
      return <tr key={i}><td colSpan={4} className="py-1.5 px-3 font-semibold text-slate-700">{r.title}</td></tr>;
    const cls = r.kind === "total" ? "bg-indigo-50/40 font-bold"
      : r.kind === "subtotal" ? "bg-slate-50/50 font-semibold" : "hover:bg-slate-50/40";
    const indent = r.kind === "item";
    const muted = r.current === null || r.current === undefined;
    return (
      <tr key={i} className={cls}>
        <td className={`py-1.5 px-3 ${indent ? "pl-7 " + (muted ? "text-slate-400" : "text-slate-600") : "text-slate-800"}`}>
          {r.label} {r.code && <span className="font-mono text-slate-300 text-[10px]">{r.code}</span>}
        </td>
        <td className="py-1.5 px-3 text-right font-mono text-slate-500">{money(r.current)}</td>
        <td className="py-1.5 px-3 text-right font-mono font-bold text-slate-900">{money(r.target)}</td>
        <td className="py-1.5 px-3 text-right">{deltaCell(r.delta)}</td>
      </tr>
    );
  };

  const rows: TargetReportRow[] = report.rows || (report.lines || []).map((l) => ({ kind: "line", ...l }));
  const incomeRows: TargetReportRow[] = report.income?.rows
    || (report.income?.lines || []).map((l) => ({ kind: "line", ...l }));

  const Head = () => (
    <thead>
      <tr className="bg-slate-50/70 text-slate-500 font-mono text-[10px] uppercase">
        <th className="text-left py-2 px-3 font-bold">Статья</th>
        <th className="text-right py-2 px-3 font-bold">Сейчас</th>
        <th className="text-right py-2 px-3 font-bold">Цель</th>
        <th className="text-right py-2 px-3 font-bold">Δ</th>
      </tr>
    </thead>
  );

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-50 bg-slate-50/60 flex items-center gap-2 flex-wrap">
        <Target className="w-4 h-4 text-emerald-600" />
        <h4 className="font-bold font-display text-sm text-slate-900">{title || "Рекомендованный баланс"} на {report.periodLabel}</h4>
        {report.form && <span className="text-[10px] font-mono bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">форма {report.form}</span>}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] font-mono text-slate-400">{report.periodEnd}</span>
          {onRecalc && (
            <button onClick={onRecalc} disabled={recalcBusy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-[11px] font-bold rounded-lg cursor-pointer">
              {recalcBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calculator className="w-3.5 h-3.5" />} Пересчитать
            </button>
          )}
        </div>
      </div>

      <div className="p-5 space-y-4">
        <p className="text-xs text-slate-600 leading-relaxed">{report.summary}</p>

        <div className="flex flex-wrap gap-2">
          {Object.entries(report.targetRatios).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-100 rounded-lg px-2.5 py-1 text-[11px]">
              <span className="text-slate-500">{k}:</span><b className="text-emerald-800">{v}</b>
            </span>
          ))}
          {report.balanceCheck?.balanced && (
            <span className="inline-flex items-center gap-1 bg-indigo-50 border border-indigo-100 text-indigo-800 rounded-lg px-2.5 py-1 text-[11px]">
              <CheckCircle2 className="w-3.5 h-3.5" /> Актив = Пассив
            </span>
          )}
        </div>

        {/* Бухгалтерский баланс по форме 0710001 (полный порядок строк) */}
        <div className="overflow-x-auto rounded-xl border border-slate-100">
          <table className="w-full text-xs"><Head /><tbody className="divide-y divide-slate-50">{rows.map(rowEl)}</tbody></table>
        </div>

        {/* Отчёт о финансовых результатах (0710002) */}
        {incomeRows.length > 0 && (
          <div>
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
              <span className="text-xs font-bold text-slate-800">Отчёт о финансовых результатах</span>
              <span className="text-[10px] font-mono bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">форма {report.income?.form || "0710002"}</span>
              {report.income && <span className="text-[11px] text-emerald-700 font-semibold">рост +{report.income.growthPct}%</span>}
            </div>
            <div className="overflow-x-auto rounded-xl border border-slate-100">
              <table className="w-full text-xs"><Head /><tbody className="divide-y divide-slate-50">{incomeRows.map(rowEl)}</tbody></table>
            </div>
            {report.income && (
              <p className="t-micro text-slate-400 mt-1.5">
                Прибыль ближайшего квартала ~<b>{formatRubles(report.income.retainedToCapital)}</b> направляется в нераспределённую прибыль (стр. 1370) и увеличивает активы.
              </p>
            )}
          </div>
        )}

        <ul className="text-[11px] text-slate-400 space-y-0.5 list-disc list-inside">
          {report.assumptions.map((a, i) => <li key={i}>{a}</li>)}
        </ul>

        {onRecalc && (
          <div className="pt-1">
            <button onClick={onRecalc} disabled={recalcBusy}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-bold rounded-lg cursor-pointer">
              {recalcBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
              Пересчитать по этому варианту
            </button>
            <p className="t-micro text-slate-400 mt-1.5">Покажет, какими станут рейтинг и оценки при достижении целевого баланса и роста выручки/прибыли (проекция, в историю не сохраняется).</p>
          </div>
        )}
      </div>
    </div>
  );
}
